import type {QueryClient} from '@tanstack/react-query';
import {
  createPhotoReportHeaderPreset,
  deletePhotoReportHeaderPreset,
  getPhotoReportHeaderPreset,
  listPhotoReportHeaderPresets,
} from '@api/photo-report-header-presets/photo-report-header-presets';
import {
  createPhotoReportTemplate,
  deletePhotoReportTemplate,
  deleteOrgPhoto,
  getDefaultOrgLogo,
  setDefaultOrgLogo,
  duplicatePhotoReportTemplate,
  listOrgPhotos,
  listPhotoReportTemplates,
  migratePhotoReportTemplate,
  previewPhotoReportTemplateMigration,
  renameOrgPhoto,
  updatePhotoReportTemplate,
  uploadOrgPhoto,
  getGetPhotoReportTemplateQueryKey,
} from '@api/photo-report-templates/photo-report-templates';
import type {
  HeaderPresetCreate as GenHeaderPresetCreate,
  PhotoReportTemplateCreate as GenPhotoReportTemplateCreate,
  PhotoReportTemplateResponse,
  PhotoReportTemplateUpdate as GenPhotoReportTemplateUpdate,
} from '@api/model';
import type {
  HeaderPreset,
  HeaderPresetCreate,
  HeaderPresetDetail,
  MigrationPreview,
  OrgPhoto,
  PhotoReportTemplate,
  PhotoReportTemplateCreate,
  PhotoReportTemplateUpdate,
  PhotoRequirement,
  TemplateForm,
  TemplateTypeValue,
} from './types';

/**
 * Thin façade over the Orval-generated photo-report client. Every call routes
 * through the single Axios instance (`src/api/client.ts`), so template / preset
 * / photo mutations inherit the 401→logout redirect and the offline mutation
 * queue — which the previous hand-rolled `fetch` implementation bypassed.
 *
 * The generated wire models type `requirements`/`settings`/`elements` as opaque
 * `{ [k]: unknown }`; the editor works with the richly-typed `PhotoRequirement`
 * view model. `toEditorTemplate` (and the inline casts below) are the single
 * boundary adapter that reinterprets those fields, keeping the editor's typed
 * model intact. (Blocker #4 will make `PhotoRequirement` a discriminated union;
 * these casts stay until then.)
 */
function toEditorTemplate(res: PhotoReportTemplateResponse): PhotoReportTemplate {
  return {
    ...res,
    created_at: typeof res.created_at === 'string' ? res.created_at : '',
    updated_at: typeof res.updated_at === 'string' ? res.updated_at : '',
    // Default to [] so a response that predates the multi-project change (an
    // API without project_ids) doesn't crash the list/editor. Consumers read
    // `.length` / `.map` on this.
    project_ids: (res.project_ids ?? []) as string[],
    requirements: (res.requirements ?? []) as unknown as PhotoRequirement[],
    settings: (res.settings ?? {}) as PhotoReportTemplate['settings'],
  };
}

/**
 * Build the create/update wire body from the editor form. Both template types
 * send `service_type_ids` (DERP-226 — photo_report used to send a single
 * `service_type_id`, and sending the wrong one was rejected); an empty list is
 * the project default for photo_report and invalid for JAC.
 * `client_id` is a UI-only project filter and is never sent; `requirements` are
 * attached by the caller's save mutation (they carry the pending-upload
 * lifecycle), so both are intentionally omitted here.
 */
export function toCreateBody(form: TemplateForm): PhotoReportTemplateCreate {
  return {
    name: form.name,
    project_ids: form.project_ids,
    template_type: form.template_type,
    service_type_ids: form.service_type_ids,
    settings: form.settings,
  };
}

export const photoReportTemplatesApi = {
  async list(): Promise<PhotoReportTemplate[]> {
    const res = await listPhotoReportTemplates();
    return res.data.map(toEditorTemplate);
  },

  async create(data: PhotoReportTemplateCreate, force = false): Promise<PhotoReportTemplate> {
    const res = await createPhotoReportTemplate(
      data as unknown as GenPhotoReportTemplateCreate,
      force ? {force: true} : undefined,
    );
    return toEditorTemplate(res);
  },

  async update(id: string, data: PhotoReportTemplateUpdate): Promise<PhotoReportTemplate> {
    const res = await updatePhotoReportTemplate(
      id,
      data as unknown as GenPhotoReportTemplateUpdate,
    );
    return toEditorTemplate(res);
  },

  async duplicate(id: string): Promise<PhotoReportTemplate> {
    return toEditorTemplate(await duplicatePhotoReportTemplate(id));
  },

  /**
   * Preflight a Photo Report ⇄ JAC migration (DERP-254).
   *
   * Read-only, and gated on `:view` rather than `:edit`, so the confirmation
   * renders for anyone who can open the editor. Returns the slot clashes (each
   * naming the template that holds it), the jobs whose filled reports the flip
   * would put at risk, and `can_migrate` — the server's verdict, which is the
   * only thing a caller should block on.
   */
  previewMigration(id: string, toType: TemplateTypeValue): Promise<MigrationPreview> {
    return previewPhotoReportTemplateMigration(id, {to_type: toType});
  },

  /**
   * Flip a template to the other type, keeping its layout and service types.
   *
   * `force` takes a slot another live template holds — the API narrow-detaches
   * only the clashing (project, service type) rows, so the occupant keeps every
   * other project and service type it serves. It is named at the call site
   * rather than passed positionally: `migrate(id, 'jac', true)` reads like a
   * flag nobody can decode, and this one detaches another template's projects.
   */
  async migrate(
    id: string,
    {toType, force}: {toType: TemplateTypeValue; force: boolean},
  ): Promise<PhotoReportTemplate> {
    return toEditorTemplate(await migratePhotoReportTemplate(id, {to_type: toType}, {force}));
  },

  delete(id: string): Promise<void> {
    return deletePhotoReportTemplate(id);
  },
};

/** TanStack Query key for the org logo/photo library. */
/** The editor's own template list. */
export const TEMPLATES_QUERY_KEY = ['photo-report-templates'] as const;

/**
 * Query key for one template's migration preflight (DERP-254).
 *
 * Deliberately NOT nested under `TEMPLATES_QUERY_KEY`. A preflight is a transient
 * what-if about a template, not a read of one, and `invalidateTemplateReads` must
 * not sweep it up: the dialog holds the confirm open across its own migrate
 * request, so a prefix match would refetch the preflight from that mutation's own
 * invalidation — asking what it would mean to migrate a template that just
 * migrated, and throwing the answer away. Nothing else can change slot state
 * while the dialog is open; it is modal.
 */
export const migrationPreviewQueryKey = (id: string, toType: TemplateTypeValue) =>
  ['photo-report-migration-preview', id, toType] as const;

/**
 * Prefix of the generated `getPhotoReportTemplateByProject` query key — the job
 * report tab's read, keyed by URL rather than by name.
 *
 * Pinned by a test against the generated key builder, because it is a literal
 * copy of a path this module does not own.
 */
export const TEMPLATE_BY_PROJECT_KEY_PREFIX = '/photo-report-templates/by-project/';

/** A template id, which is the only thing that can sit in the by-id key's slot. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Is this the generated `getPhotoReportTemplate` key — one template, read by id?
 *
 * The job report tab reads that key now, keyed by the report's frozen
 * `template_id`, so an editor save has to sweep it or the fill page shows the
 * pre-edit layout until the tab is remounted — the same DERP-225 staleness the
 * by-project prefix above exists to prevent, at a key that did not exist when it
 * was written.
 *
 * Recognised by asking the GENERATED BUILDER rather than by matching a copy of
 * the path: the id is pulled back out of the key and fed to the builder, and the
 * key is a by-id key exactly when the builder agrees. That way this module holds
 * no literal it does not own, and a path change in the spec fails here instead of
 * quietly never matching again.
 *
 * The UUID guard is the part the builder cannot supply. The siblings under this
 * path are literals — `/photo-report-templates/photos/` is one segment too, and
 * it round-trips through the builder just as happily — so a bare round-trip
 * would sweep the whole org photo library on every template save.
 */
export function isTemplateByIdKey(key: unknown): boolean {
  if (typeof key !== 'string') return false;
  const id = key.split('/').at(-2);
  if (!id || !UUID.test(id)) return false;
  return getGetPhotoReportTemplateQueryKey(id)[0] === key;
}

/**
 * Invalidate everything that reads a template, not just the editor's list.
 *
 * TanStack Query prefix-matches ARRAY ELEMENTS, and `'photo-report-templates'`
 * is not equal to `'/photo-report-templates/by-project/…'` — so invalidating
 * `TEMPLATES_QUERY_KEY` alone never touched the by-project cache, and a saved
 * template took up to the fill tab's 5-minute staleTime to appear (DERP-225).
 *
 * The predicate covers every project + service-type variant, which is right: an
 * edit can change which template resolves for any project the template is linked
 * to, including one just unlinked from it.
 */
export function invalidateTemplateReads(qc: QueryClient): void {
  void qc.invalidateQueries({queryKey: TEMPLATES_QUERY_KEY});
  void qc.invalidateQueries({
    predicate: (q) =>
      (typeof q.queryKey[0] === 'string' &&
        q.queryKey[0].startsWith(TEMPLATE_BY_PROJECT_KEY_PREFIX)) ||
      isTemplateByIdKey(q.queryKey[0]),
  });
}

export const ORG_PHOTOS_QUERY_KEY = ['org-photos'] as const;

/**
 * Query key for the logo URLs one layout needs.
 *
 * Keyed on the ids themselves, sorted, because that IS the input: two layouts
 * stamping the same logos share the answer, and a layout that gains one asks a
 * different question. Not nested under `ORG_PHOTOS_QUERY_KEY` — the library's
 * own mutations (rename, upload) do not change which URL an id resolves to, and
 * the ones that DO invalidate this by prefix below.
 */
export const orgLogoUrlsQueryKey = (ids: Iterable<string>) =>
  ['org-logo-urls', [...ids].sort().join(',')] as const;

/**
 * The whole read, ready to spread into `useQuery` — key, fetch and freshness
 * policy in one place.
 *
 * Exported as options rather than as the bare fetcher because the freshness IS
 * part of the contract and belongs with the thing that knows why: a presigned
 * URL expires in about fifteen minutes, so a surface holding one open for longer
 * has to re-mint it. (One of the `staleTime` overrides `main.tsx` sanctions by
 * name — a report-fill lookup.) `enabled` falls out of the ids: a layout that
 * stamps nothing asks nothing, which is also what covers the render before the
 * template has arrived.
 */
export const orgLogoUrlsQueryOptions = (ids: Set<string>) => ({
  queryKey: orgLogoUrlsQueryKey(ids),
  queryFn: () => orgPhotosApi.logoUrlsFor(ids),
  enabled: ids.size > 0,
  staleTime: 5 * 60 * 1000,
});

/** Rows the API returns per page of the photo library, whatever `limit` asks
 *  for. Named so the paging below reads as "one page at a time". */
const ORG_PHOTOS_PAGE_SIZE = 100;

/**
 * Pages walked before `logoUrlsFor` gives up — 5000 photos deep.
 *
 * A backstop, not a policy: it exists so a library that grows past anything we
 * anticipated degrades to an unrefreshed logo instead of an unbounded request
 * loop on a technician's phone. It THROWS when it trips, because a silent cap
 * reads as "the id is not in the library" when it means "we stopped looking" --
 * and those two now have different consequences (see `logoUrlsFor`).
 */
const MAX_PAGES = 50;

export const orgPhotosApi = {
  async list(): Promise<OrgPhoto[]> {
    const res = await listOrgPhotos();
    return res.data;
  },

  /**
   * Fresh presigned URLs for the logos a layout stamps, by id.
   *
   * **A search for named ids, not a read of the first page.** A stamped logo
   * caches its presigned URL beside its `logo_id` and that URL expires (~15
   * min), so anything drawing a saved template has to re-mint it — and the only
   * endpoint that can is the photo library, which the API pages at 100 rows,
   * NEWEST first. Both callers used to take page one and build the map from it,
   * which quietly stopped resolving anything older: the company logo is the
   * photo most likely to be the OLDEST row, because it is the first thing an org
   * uploads. Past one page of photos every stamped logo silently vanished from
   * the job report — the same failure `getDefault` was fixed for, arriving by
   * the same route one layer along.
   *
   * Stops as soon as every id is accounted for, so the usual one-logo template
   * costs one request; walks on only while something is still missing.
   *
   * TODO(DERP-271): a `GET /photo-report-templates/photos/?ids=…` deletes this
   * outright — resolving N ids should not mean paging a library. The query
   * already exists server-side: `_fresh_logo_urls(logo_ids)` in
   * `modules/projects/services/job_report_share_service.py` resolves exactly
   * this set in ONE round trip, written for the share link because an
   * unauthenticated visitor cannot page anything. Exposing it on the
   * authenticated route is the whole change. Until then the paging lives here,
   * once, rather than in each caller.
   *
   * **What it costs while it stays.** Up to `MAX_PAGES` sequential round trips
   * on whatever device is opening the report — a phone on a roof, usually. The
   * early exit means a one-logo template pays for one, which is why this is
   * shippable at all; it is not why it is right. A workaround that works is a
   * workaround nobody comes back to, which is why it is a ticket and not only
   * this comment.
   */
  async logoUrlsFor(ids: Iterable<string>): Promise<Map<string, string>> {
    const wanted = new Set(ids);
    const found = new Map<string, string>();
    if (wanted.size === 0) return found;

    for (let page = 1; page <= MAX_PAGES; page++) {
      const res = await listOrgPhotos({page, limit: ORG_PHOTOS_PAGE_SIZE});
      for (const photo of res.data) {
        if (wanted.delete(photo.id)) found.set(photo.id, photo.download_url);
      }
      if (wanted.size === 0 || !res.pagination.has_next) return found;
      if (page === MAX_PAGES) {
        // **Giving up is a failure, not a partial answer.** An id absent from
        // the returned map MEANS the library does not have it -- that is what
        // `rehydrateLogoUrls` clears a stored `logo_url` on. A truncated search
        // would make "we stopped looking" indistinguishable from "deleted" and
        // strip good logos off a template nobody touched. Throwing puts both
        // callers on the degradation they already document: the editor's
        // `catch` keeps the stored (unrefreshed) URLs, and the report document
        // gets no map and leaves its requirements alone.
        throw new Error(
          `Stopped searching the photo library after ${MAX_PAGES} pages with ` +
            `${wanted.size} stamped logo(s) unresolved; leaving stored URLs alone.`,
        );
      }
    }
    return found;
  },

  upload(file: File): Promise<OrgPhoto> {
    return uploadOrgPhoto({file});
  },

  rename(id: string, name: string): Promise<OrgPhoto> {
    return renameOrgPhoto(id, {name});
  },

  delete(id: string): Promise<void> {
    return deleteOrgPhoto(id);
  },

  /** Make this photo the org's default logo, replacing whatever was. */
  setDefault(id: string): Promise<OrgPhoto> {
    return setDefaultOrgLogo(id);
  },

  /**
   * The org's default logo, or `null` if it has not chosen one.
   *
   * **A read of its own, not a scan of the photo list.** This used to be
   * `photos.find((p) => p.is_default)` over `list()`, on the grounds that
   * whoever needs the default already needs the list, and one request cannot
   * disagree with itself. What that missed is that the list is *paginated* --
   * the API clamps it to 100 rows, newest first -- and the default is the photo
   * most likely to be the oldest row in it, because the company logo is the
   * first thing an org uploads. Past one page of photos the scan quietly
   * returned nothing and every Logo toggle stamped no logo at all: the failure
   * the default exists to prevent, arriving exactly when an org is big enough to
   * hit it.
   *
   * `is_default` is still on the list rows and the library still marks them --
   * this is for the surfaces that need the default *itself*.
   */
  getDefault(): Promise<OrgPhoto | null> {
    return getDefaultOrgLogo();
  },
};

/**
 * TanStack Query key for the org's default logo. Invalidated alongside
 * `ORG_PHOTOS_QUERY_KEY` wherever a default is set or a photo is deleted --
 * either can change the answer.
 */
export const ORG_DEFAULT_LOGO_QUERY_KEY = ['org-default-logo'] as const;

/** TanStack Query key for the reusable header-preset list. */
export const HEADER_PRESETS_QUERY_KEY = ['photo-report-header-presets'] as const;

export const headerPresetsApi = {
  async list(): Promise<HeaderPreset[]> {
    const res = await listPhotoReportHeaderPresets();
    return res.data;
  },

  async get(id: string): Promise<HeaderPresetDetail> {
    const res = await getPhotoReportHeaderPreset(id);
    return {...res, elements: (res.elements ?? []) as unknown as PhotoRequirement[]};
  },

  create(data: HeaderPresetCreate): Promise<HeaderPreset> {
    return createPhotoReportHeaderPreset(data as unknown as GenHeaderPresetCreate);
  },

  delete(id: string): Promise<void> {
    return deletePhotoReportHeaderPreset(id);
  },
};
